import { create } from 'zustand';
import { useCampaignStore } from './campaignStore';

export interface WindowPosition {
  x: number;
  y: number;
}

export interface WindowSize {
  width: number;
  height: number;
}

export type WindowState = 'normal' | 'minimized' | 'maximized' | 'docked';

export interface WindowConfig {
  id: string;
  type: 'character-sheet' | 'chat' | 'dm-console' | 'dice' | 'notes' | 'custom';
  title: string;
  position: WindowPosition;
  size: WindowSize;
  minSize?: WindowSize;
  maxSize?: WindowSize;
  state: WindowState;
  zIndex: number;
  isVisible: boolean;
  canClose: boolean;
  canMinimize: boolean;
  canMaximize: boolean;
  canResize: boolean;
  data?: Record<string, unknown>; // Window-specific data (e.g., characterId)
}

interface WindowStore {
  windows: Record<string, WindowConfig>;
  activeWindowId: string | null;
  nextZIndex: number;

  // Window actions
  openWindow: (config: Omit<WindowConfig, 'zIndex'>) => void;
  closeWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  moveWindow: (id: string, position: WindowPosition) => void;
  resizeWindow: (id: string, size: WindowSize) => void;
  setWindowState: (id: string, state: WindowState) => void;
  toggleWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  maximizeWindow: (id: string) => void;
  restoreWindow: (id: string) => void;
  updateWindowData: (id: string, data: Record<string, unknown>) => void;

  dockWindow: (id: string) => void;
  undockWindow: (id: string) => void;

  // Query
  getWindow: (id: string) => WindowConfig | undefined;
  getWindowsByType: (type: WindowConfig['type']) => WindowConfig[];
  isWindowOpen: (id: string) => boolean;
  getDockedCharacterSheets: () => WindowConfig[];
}

const DEFAULT_MIN_SIZE: WindowSize = { width: 200, height: 150 };
const DEFAULT_SIZE: WindowSize = { width: 400, height: 500 };

export const useWindowStore = create<WindowStore>((set, get) => ({
  windows: {},
  activeWindowId: null,
  nextZIndex: 100,

  openWindow: (config) => {
    const { windows, nextZIndex } = get();

    // If window already exists, just focus it
    if (windows[config.id]) {
      get().focusWindow(config.id);
      return;
    }

    set({
      windows: {
        ...windows,
        [config.id]: {
          ...config,
          zIndex: nextZIndex,
          minSize: config.minSize || DEFAULT_MIN_SIZE,
          size: config.size || DEFAULT_SIZE,
        },
      },
      activeWindowId: config.id,
      nextZIndex: nextZIndex + 1,
    });
  },

  closeWindow: (id) => {
    const { windows, activeWindowId } = get();
    const { [id]: _, ...rest } = windows;

    set({
      windows: rest,
      activeWindowId: activeWindowId === id ? null : activeWindowId,
    });
  },

  focusWindow: (id) => {
    const { windows, nextZIndex } = get();
    const window = windows[id];

    if (!window) return;

    set({
      windows: {
        ...windows,
        [id]: {
          ...window,
          zIndex: nextZIndex,
          isVisible: true,
        },
      },
      activeWindowId: id,
      nextZIndex: nextZIndex + 1,
    });
  },

  moveWindow: (id, position) => {
    const { windows } = get();
    const window = windows[id];

    if (!window) return;

    set({
      windows: {
        ...windows,
        [id]: { ...window, position },
      },
    });
  },

  resizeWindow: (id, size) => {
    const { windows } = get();
    const window = windows[id];

    if (!window) return;

    // Clamp to min/max
    const minSize = window.minSize || DEFAULT_MIN_SIZE;
    const maxSize = window.maxSize;

    const clampedSize = {
      width: Math.max(minSize.width, maxSize ? Math.min(size.width, maxSize.width) : size.width),
      height: Math.max(minSize.height, maxSize ? Math.min(size.height, maxSize.height) : size.height),
    };

    set({
      windows: {
        ...windows,
        [id]: { ...window, size: clampedSize },
      },
    });
  },

  setWindowState: (id, state) => {
    const { windows } = get();
    const window = windows[id];

    if (!window) return;

    set({
      windows: {
        ...windows,
        [id]: { ...window, state },
      },
    });
  },

  toggleWindow: (id) => {
    const { windows } = get();
    const window = windows[id];

    if (!window) return;

    set({
      windows: {
        ...windows,
        [id]: { ...window, isVisible: !window.isVisible },
      },
    });
  },

  minimizeWindow: (id) => {
    const { windows, activeWindowId } = get();
    const window = windows[id];

    if (!window) return;

    set({
      windows: {
        ...windows,
        [id]: { ...window, state: 'minimized' },
      },
      activeWindowId: activeWindowId === id ? null : activeWindowId,
    });
  },

  maximizeWindow: (id) => {
    const { windows, nextZIndex } = get();
    const window = windows[id];

    if (!window) return;

    set({
      windows: {
        ...windows,
        [id]: {
          ...window,
          state: 'maximized',
          zIndex: nextZIndex,
        },
      },
      activeWindowId: id,
      nextZIndex: nextZIndex + 1,
    });
  },

  restoreWindow: (id) => {
    const { windows, nextZIndex } = get();
    const window = windows[id];

    if (!window) return;

    set({
      windows: {
        ...windows,
        [id]: {
          ...window,
          state: 'normal',
          zIndex: nextZIndex,
        },
      },
      activeWindowId: id,
      nextZIndex: nextZIndex + 1,
    });
  },

  updateWindowData: (id, data) => {
    const { windows } = get();
    const window = windows[id];

    if (!window) return;

    set({
      windows: {
        ...windows,
        [id]: {
          ...window,
          data: { ...window.data, ...data },
        },
      },
    });
  },

  dockWindow: (id) => {
    const { windows } = get();
    const window = windows[id];
    if (!window) return;

    set({
      windows: {
        ...windows,
        [id]: { ...window, state: 'docked' },
      },
    });
  },

  undockWindow: (id) => {
    const { windows, nextZIndex } = get();
    const window = windows[id];
    if (!window) return;

    // Center on screen at 500x700
    const x = Math.max(100, (globalThis.innerWidth - 500) / 2);
    const y = Math.max(50, (globalThis.innerHeight - 700) / 2);

    set({
      windows: {
        ...windows,
        [id]: {
          ...window,
          state: 'normal',
          position: { x, y },
          size: { width: 500, height: 700 },
          zIndex: nextZIndex,
        },
      },
      activeWindowId: id,
      nextZIndex: nextZIndex + 1,
    });
  },

  getWindow: (id) => get().windows[id],

  getWindowsByType: (type) =>
    Object.values(get().windows).filter((w) => w.type === type),

  isWindowOpen: (id) => !!get().windows[id],

  getDockedCharacterSheets: () =>
    Object.values(get().windows).filter(
      (w) => w.type === 'character-sheet' && w.state === 'docked'
    ),
}));

/**
 * Helper to generate a character sheet window ID
 */
export function getCharacterWindowId(characterId: string): string {
  return `character-sheet-${characterId}`;
}

/**
 * Track which character sheets have open popup windows.
 * Separate lightweight store so PartyMemberRow can subscribe without
 * re-rendering on every window-manager change.
 */
interface OpenSheetStore {
  openSheetIds: Set<string>;
  isSheetOpen: (characterId: string) => boolean;
}

export const useOpenSheetStore = create<OpenSheetStore>((_set, get) => ({
  openSheetIds: new Set(),
  isSheetOpen: (characterId) => get().openSheetIds.has(characterId),
}));

// Module-level map of characterId → popup WindowProxy
const _sheetPopups = new Map<string, Window>();
let _pollTimer: ReturnType<typeof setInterval> | null = null;

function startPolling() {
  if (_pollTimer) return;
  _pollTimer = setInterval(() => {
    let changed = false;
    for (const [id, popup] of _sheetPopups) {
      if (popup.closed) {
        _sheetPopups.delete(id);
        changed = true;
      }
    }
    if (changed) {
      useOpenSheetStore.setState({ openSheetIds: new Set(_sheetPopups.keys()) });
    }
    if (_sheetPopups.size === 0 && _pollTimer) {
      clearInterval(_pollTimer);
      _pollTimer = null;
    }
  }, 1000);
}

/**
 * Helper to open a character sheet in a separate browser window,
 * positioned to the right of the main Machina window.
 */
export function openCharacterSheet(
  characterId: string,
): void {
  const campaignId = useCampaignStore.getState().currentCampaignId;
  if (!campaignId) return;

  const sheetUrl = `${window.location.origin}${window.location.pathname}#sheet/${campaignId}/${characterId}`;

  // Position the popup flush to the right of the main window
  const popupWidth = 520;
  const popupHeight = window.outerHeight;
  const popupLeft = window.screenX + window.outerWidth;
  const popupTop = window.screenY;

  const popup = window.open(
    sheetUrl,
    `machina-sheet-${characterId}`,
    `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},menubar=no,toolbar=no,location=no,status=no`
  );

  if (popup) {
    _sheetPopups.set(characterId, popup);
    useOpenSheetStore.setState({ openSheetIds: new Set(_sheetPopups.keys()) });
    startPolling();
  }
}

// =============================================================================
// GM ASSISTANT POPUP — singleton
// =============================================================================

let _gmPopup: Window | null = null;
let _gmPollTimer: ReturnType<typeof setInterval> | null = null;

export function openGMAssistant(): void {
  const campaignId = useCampaignStore.getState().currentCampaignId;
  if (!campaignId) return;

  // If already open, just focus
  if (_gmPopup && !_gmPopup.closed) {
    _gmPopup.focus();
    return;
  }

  const url = `${window.location.origin}${window.location.pathname}#gm/${campaignId}`;

  const popupWidth = 600;
  const popupHeight = window.outerHeight;
  const popupLeft = window.screenX + window.outerWidth;
  const popupTop = window.screenY;

  _gmPopup = window.open(
    url,
    'machina-gm-assistant',
    `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},menubar=no,toolbar=no,location=no,status=no`
  );

  if (_gmPopup) {
    // Poll to detect closed window
    _gmPollTimer = setInterval(() => {
      if (_gmPopup?.closed) {
        _gmPopup = null;
        if (_gmPollTimer) {
          clearInterval(_gmPollTimer);
          _gmPollTimer = null;
        }
      }
    }, 1000);
  }
}
